import React, { useEffect, useRef, useState } from 'react'

type QueueTimeSlotProps = {
  time: string
  minutes: number | null
  onClick: () => void
  noData: boolean
}

export default function QueueTimeSlot({
  time,
  minutes,
  onClick,
  noData,
}: QueueTimeSlotProps) {
  let slot = null
  const [displayedMinutes, setDisplayedMinutes] = useState(0)
  const [displayedPercent, setDisplayedPercent] = useState(0)
  const previousAnimatedValue = useRef(0)
  const clampedMinutes = minutes === null ? 0 : Math.min(120, minutes)

  useEffect(() => {
    const start = previousAnimatedValue.current
    const end = clampedMinutes
    const durationMs = 300
    const startTime = performance.now()
    let frameId = 0

    const tick = (now: number) => {
      const t = Math.min(1, (now - startTime) / durationMs)
      const eased = 1 - Math.pow(1 - t, 3)
      const nextRawValue = start + (end - start) * eased
      previousAnimatedValue.current = nextRawValue
      setDisplayedMinutes(Math.round(nextRawValue))
      setDisplayedPercent((nextRawValue / 120) * 100)
      if (t < 1) frameId = requestAnimationFrame(tick)
    }

    frameId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameId)
  }, [clampedMinutes])

  if (minutes !== null) {
    const slotMinutes = minutes
    const percent = (Math.min(120, slotMinutes) / 120) * 100
    const smileyClass =
      percent > 0 && percent < 45
        ? 'QueueTimeSlot__Minutes--ok'
        : minutes === 0
          ? 'QueueTimeSlot__Minutes--good'
          : 'QueueTimeSlot__Minutes--bad'

    slot = (
      <div
        onClick={() => onClick()}
        className={`QueueTimeSlot__Minutes $ ${smileyClass}`}
        style={{
          width: `${displayedPercent}%`,
          backgroundColor:
            minutes === 0 ? '#60B356' : percent >= 45 ? '#E55240' : '#E99F96',
        }}
      >
        {minutes === 0 ? '' : displayedMinutes}
        {minutes > 120 ? '+' : ''}
      </div>
    )
  } else {
    slot = (
      <div
        className="QueueTimeSlot__Minutes QueueTimeSlot__Minutes--closed"
        style={{
          backgroundColor: 'transparent',
          color: 'rgba(45, 72, 100, 0.56)',
        }}
      >
        {noData
          ? window.jsProps.strings.noPredictionData
          : window.jsProps.strings.closed}
      </div>
    )
  }

  return (
    <div className="QueueTimeSlot">
      <div className="QueueTimeSlot__Time">{time}</div>
      <div className="QueueTimeSlot__MinutesContainer">{slot}</div>
    </div>
  )
}
